import { useRef } from 'react';
import {
	fireTrackAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';

import {
	useFireCrossFlowAnalyticsEvents,
	FIRST_PARTY_CCP_PRODUCT_IDS,
} from '@atlassian/surface-analytics';

export const EXPERIMENT_ID = 'jira_your_work_recommendations_nba';

/**
 * cardVersion can be
 * `cross-flow` when the crossflow card is shown
 * `expansion` when the expansion card is shown
 * `null-card-waiting-period` when user is in the waiting period after crossflowing
 * `null-card-control` (control) in the case of control tenants
 * `null-card-error` (experiment) when the experiment card fails to show because of error
 * `null-card-missing-data` for experiment when the card isn't shown e.g. recommendations returns an empty array
 */
type UseFireExposureEventOnceProps = {
	cohort: string;
	cardVersion:
		| 'expansion'
		| 'cross-flow'
		| 'null-card-waiting-period'
		| 'null-card-control'
		| 'null-card-error'
		| 'null-card-missing-data';
};

const env = process.env.NODE_ENV === 'production' ? 'prod' : 'staging';
const JIRA_PRODUCT_DISCOVERY_CCP_PRODUCT_ID =
	FIRST_PARTY_CCP_PRODUCT_IDS[env].jira_product_discovery;

export const useFireExposureEventOnce = () => {
	const { fireViewedUIEvent } = useFireCrossFlowAnalyticsEvents();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isFired = useRef(false);

	return ({ cohort, cardVersion }: UseFireExposureEventOnceProps) => {
		if ((cohort === 'control' || cohort === 'experiment') && !isFired.current) {
			if (cardVersion === 'cross-flow') {
				fireViewedUIEvent(
					{
						action: 'viewed',
						actionSubject: 'card',
						actionSubjectId: 'jiraYourWorkRecommendationsExperiment',
					},
					{
						recommendedProductIds: [JIRA_PRODUCT_DISCOVERY_CCP_PRODUCT_ID],
						source: 'jiraYourWorkRecommendationsExperiment',
					},
					{
						cohort,
						cardVersion,
						growthInitiative: 'cross-flow',
						flagKey: EXPERIMENT_ID,
					},
				);
			} else {
				fireTrackAnalytics(
					createAnalyticsEvent({
						actionSubject: 'feature',
						action: 'exposed',
					}),
					'feature exposed',
					{
						cohort,
						exposedTo: cardVersion,
					},
				);
			}
			isFired.current = true;
		}
	};
};

type UseFireButtonClickEventProps<E> = {
	eventName: E;
	cohort: string;
};

export const useFireButtonClickEvent = ({
	eventName,
	cohort,
}: UseFireButtonClickEventProps<string>) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return () =>
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			eventName,
			{
				growthInitiative: 'cross-flow',
				flagKey: EXPERIMENT_ID,
				cohort,
			},
		);
};

export const useFireUCIAButtonEvent = ({
	eventName,
	cohort,
}: UseFireButtonClickEventProps<'tryNow' | 'learnMore'>) => {
	const { fireInteractedUIEvent } = useFireCrossFlowAnalyticsEvents();

	return () =>
		fireInteractedUIEvent(
			{
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: eventName,
			},
			{
				recommendedProductIds: [JIRA_PRODUCT_DISCOVERY_CCP_PRODUCT_ID],
				targetProductId: JIRA_PRODUCT_DISCOVERY_CCP_PRODUCT_ID,
				source: 'jiraYourWorkRecommendationsExperimentCard',
			},
			{ cohort, growthInitiative: 'cross-flow', flagKey: EXPERIMENT_ID },
		);
};
